import { Row, Column } from '@simplywallst/ui-core';
import {
  ChecksTitle,
  CheckRow,
  CheckName,
  CheckArrow,
  IconFallback,
} from './styled';
import { useTheme } from '@simplywallst/ui-core';
import { useScrollIntoView } from '@/hooks/useScrollIntoView';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import {
  VALUATION_PB_RATIO,
  VALUATION_PE_GROWTH,
  VALUATION_PE_RATIO,
  VALUATION_FAIR_RATIO,
  VALUATION_PRICE_VS_FAIR_RATIO,
  ANALYST_PRICE_TARGETS,
  FUTURE_GROWTH,
  FUTURE_RETURN_EQUITY,
  PAST_EARNINGS_GROWTH,
  PAST_EARNINGS_REVENUE,
  PAST_ROE,
  HEALTH_DEBT_HISTORY,
  HEALTH_FINANCIAL_POSITION,
  DIVIDEND_YIELD,
  DIVIDEND_STABILITY_GROWTH,
  DIVIDEND_PAYOUT_CURRENT,
  DIVIDEND_PAYOUT_FUTURE,
  HEALTH_FINANCIAL_INSTITUTION,
  VALUATION_RATIO_VS_PEERS,
  VALUATION_RATIO_VS_INDUSTRY,
  HEALTH_CASH_RUNWAY,
  MANAGEMENT_CEO_SALARY,
  MANAGEMENT_TENURE,
  MANAGEMENT_BOARD_TENURE,
} from '@/constants/anchors';
import type { StatementChecks as Checks } from '@/pages/CompanyReport/components/Introduction/utils';

const StatementPositiveSolid = loadable(
  () => import('@simplywallst/ui-core/icons/StatementPositiveSolid'),
  {
    ssr: false,
    fallback: <IconFallback />,
  }
);
const StatementNegativeSolid = loadable(
  () => import('@simplywallst/ui-core/icons/StatementNegativeSolid'),
  {
    ssr: false,
    fallback: <IconFallback />,
  }
);

const mapHashToSection = (hash: string | undefined) => {
  switch (hash) {
    case 'IsGoodValueComparingPreferredMultipleToPeersAverageValue':
      return VALUATION_RATIO_VS_PEERS;
    case 'IsGoodValueComparingPreferredMultipleToIndustry':
      return VALUATION_RATIO_VS_INDUSTRY;
    case 'IsGoodValueComparingRatioToFairRatio':
      return VALUATION_FAIR_RATIO;
    case 'IsUndervaluedBasedOnDCF':
    case 'IsHighlyUndervaluedBasedOnDCF':
      return VALUATION_PRICE_VS_FAIR_RATIO;
    case 'IsAnalystForecastTrustworthy':
      return ANALYST_PRICE_TARGETS;
    case 'IsUndervaluedOnPERelativeToPeers':
    case 'IsUndervaluedOnPERelativeToMarket':
      return VALUATION_PE_RATIO;
    case 'IsUndervaluedBasedOnPEG':
      return VALUATION_PE_GROWTH;
    case 'IsUndervaluedBasedOnPB':
      return VALUATION_PB_RATIO;
    case 'IsExpectedProfitGrowthAboveRiskFreeRate':
    case 'IsExpectedAnnualProfitGrowthAboveMarket':
    case 'IsExpectedAnnualProfitGrowthHigh':
    case 'IsExpectedRevenueGrowthAboveMarket':
    case 'IsExpectedRevenueGrowthHigh':
      return FUTURE_GROWTH;
    case 'IsReturnOnEquityForecastAboveBenchmark':
      return FUTURE_RETURN_EQUITY;
    case 'HasHighQualityPastEarnings':
    case 'HasPastNetProfitMarginImprovedOverLastYear':
      return PAST_EARNINGS_REVENUE;
    case 'HasGrownProfitsOverPast5Years':
    case 'HasProfitGrowthAccelerated':
    case 'IsGrowingFasterThanIndustry':
      return PAST_EARNINGS_GROWTH;
    case 'IsReturnOnEquityAboveThreshold':
      return PAST_ROE;
    case 'AreShortTermLiabilitiesCovered':
    case 'AreLongTermLiabilitiesCovered':
      return HEALTH_FINANCIAL_POSITION;
    case 'IsDebtLevelAppropriate':
    case 'HasDebtReducedOverTime':
    case 'IsDebtCoveredByCashflow':
    case 'IsInterestCoveredByProfit':
      return HEALTH_DEBT_HISTORY;
    case 'HasAnAppropriateLevelOfAssets':
    case 'HasAppropriateBadLoanAllowance':
    case 'HasPrimarilyLowRiskFunding':
    case 'HasAppropriateLoanLevel':
    case 'HasPrimarilyDepositFunding':
    case 'HasAppropriateNonPerformingLoans':
      return HEALTH_FINANCIAL_INSTITUTION;
    case 'HasCashRunwayIfStable':
    case 'HasCashRunwayIfGrowing':
      return HEALTH_CASH_RUNWAY;
    case 'IsDividendSignificant':
    case 'IsDividendYieldTopTier':
      return DIVIDEND_YIELD;
    case 'IsDividendStable':
    case 'IsDividendGrowing':
      return DIVIDEND_STABILITY_GROWTH;
    case 'IsDividendCovered':
      return DIVIDEND_PAYOUT_CURRENT;
    case 'IsDividendCoveredIn3Years':
    case 'IsDividendCoveredByFreeCashFlow':
      return DIVIDEND_PAYOUT_FUTURE;
    case 'IsCEOCompensationAppropriate':
    case 'IsCEOCompensationChangeJustified':
      return MANAGEMENT_CEO_SALARY;
    case 'IsManagementTeamSeasoned':
      return MANAGEMENT_TENURE;
    case 'IsBoardSeasoned':
      return MANAGEMENT_BOARD_TENURE;

    default:
      return '';
  }
};

interface Props {
  title: string;
  testId: string;
  checks: Checks[];
  score: number;
}

export const StatementChecks = ({ title, testId, checks, score }: Props) => {
  const theme = useTheme();
  const tracking = useTracking();
  const scroller = useScrollIntoView();

  return (
    <Row alignItem="flex-end">
      <ChecksTitle data-cy-id={testId}>
        {title}
        <span>{` ${score}/${checks.length}`}</span>
      </ChecksTitle>
      <Column as="ul">
        {checks.map((check, i) => {
          const { title, hash } = check;
          const sectionHash = mapHashToSection(hash);
          return (
            <CheckRow
              as="li"
              data-cy-id={`statement-check-${check.hash}`}
              key={`${sectionHash}-${i}`}
              alignItem="center"
              onClick={() => {
                if (sectionHash) {
                  scroller(`#${sectionHash}`);
                  tracking.trackEvent({
                    action: 'click',
                    modifier: 'analysis_checks',
                    properties: hash,
                  });
                }
              }}
            >
              {check.mark === 'PASS' ? (
                <StatementPositiveSolid
                  fill={theme.color.good}
                  width={16}
                  data-cy-id="pass"
                />
              ) : (
                <StatementNegativeSolid
                  fill={theme.color.bad}
                  width={16}
                  data-cy-id="fail"
                />
              )}
              <CheckName>{title}</CheckName>
              <CheckArrow
                className="checkarrow"
                fill={theme.color.whiteRGBA(0.5)}
              />
            </CheckRow>
          );
        })}
      </Column>
    </Row>
  );
};
